import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchProductListForWebThunk } from "slices/ProductWeb/thunk";
import type { RootState } from "store";
import type { Product } from "services/productService";
import DashboardHeader from "../../Components/Common/DashboardHeader";
import LeadModal from "./lead";

const InsuranceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  const productWebSelector = useMemo(
    () =>
      createSelector(
        (state: RootState) => (state as any).productWeb,
        (productWeb) => ({
          products: (productWeb?.products || []) as Product[],
          loading: productWeb?.loading,
          error: productWeb?.error,
        })
      ),
    []
  );

  const { products, loading, error } = useSelector(productWebSelector);

  useEffect(() => {
    dispatch(
      fetchProductListForWebThunk({
        page: 1,
        per_page: 1000, 
        product_code: "",
        product_category: "",
        product_name: "",
      })
    );
  }, [dispatch, id]);

  const productId = Number(id);
  const product = products.find((p) => p.id === productId);

  const handleLeadModalClose = () => {
    setIsLeadModalOpen(false);
  };

  const handleLeadSubmit = (values: { name: string; email: string; mobileNumber: string; dialCode: string; countryCode: string }) => {
    console.log("Lead form submitted:", values);
    console.log("Product ID:", productId);
  };

  if (loading) return <div className="text-center py-5">Loading...</div>;
  if (error) return <div className="text-danger text-center">{error}</div>;
  if (!product) return <div className="text-center">Product not found</div>;

  const additional = product.product_additional_info;
  const eligibility = product.product_eligibility?.[0];

  const planTypeDisplay = product.plan_type || "-";
  const planTypeParts = planTypeDisplay.split(" ");
  const planTypeFirst = planTypeParts[0] || "";
  const planTypeRest = planTypeParts.slice(1).join(" ") || "";

  return (
    <div className="insurance-detail-page">
      <DashboardHeader />

      <div className="breadcrumbs">
        <Container>
          <a href="https://amanatfintech.com/" target="_blank" rel="noopener noreferrer">Home</a>
          <span className="separator"> &gt; </span>
          <Link to="/insurance">Browse</Link>
          <span className="separator"> &gt; </span>
          <span>Insurance</span>
        </Container>
      </div>

      <Container className="py-2 py-md-3">
        <Card className="insurance-detail-hero mb-3">
          <div className="hero-illustration">
            {product.product_image_url && (
              <img
                src={product.product_image_url}
                alt={product.product_name}
                className="hero-image"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <div className="hero-content">
              <h1 className="plan-title">{product.product_name}</h1>
              <p className="plan-label">Plan {product.product_code}</p>
            </div>
          </div>
        </Card>

        <Row className="g-2 mb-2">
          <Col xs="6" md="3">
            <Card className="summary-metric-card">
              <div className="metric-value-text">
                {additional?.minimum_sum_assured ? `BD ${additional.minimum_sum_assured}` : "-"}
              </div>
              <div className="metric-label-text">MIN SUM ASSURED</div>
            </Card>
          </Col>
          <Col xs="6" md="3">
            <Card className="summary-metric-card">
              <div className="metric-value-text">
                {additional?.maximum_sum_assured ? `BD ${additional.maximum_sum_assured}` : "-"}
              </div>
              <div className="metric-label-text">MAX SUM ASSURED</div>
            </Card>
          </Col>
          <Col xs="6" md="3">
            <Card className="summary-metric-card">
              <div className="metric-value-text">{additional?.policy_term || "-"}</div>
              <div className="metric-label-text">POLICY TERM</div>
            </Card>
          </Col>
          <Col xs="6" md="3">
            <Card className="summary-metric-card">
              <div className="metric-value-text plan-type-value">
                {planTypeFirst && (
                  <span className="plan-type-first">{planTypeFirst}</span>
                )}
                {planTypeRest && (
                  <span className="plan-type-rest"> {planTypeRest}</span>
                )}
                {!planTypeFirst && !planTypeRest && "-"}
              </div>
              <div className="metric-label-text">PLAN TYPE PREMIUM</div>
            </Card>
          </Col>
        </Row>

        <Row className="gy-2 mb-0">
          <Col lg="8">
            <Card className="insurance-detail-summary mb-3">
              <div className="p-2 p-md-3 border-bottom">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="section-title mb-0">Key benefits</h5>
                  {(product as any).product_pdf_url && (
                    <a href={(product as any).product_pdf_url} className="download-link" target="_blank" rel="noopener noreferrer">
                      <i className="ri-download-line me-1" />
                      Download Policy PDF
                    </a>
                  )}
                </div>
              </div>
              <div className="p-2 p-md-3">
                <Row className="gy-2 gy-md-3">
                  {(Array.isArray(product.product_benfit) ? product.product_benfit : []).map((b) => (
                    <Col md="6" key={b.id}>
                      <div className="benefit-item">
                        <div className="benefit-icon">
                          <i className="ri-shield-check-line" />
                        </div>
                        <div>
                          <h6 className="benefit-title">{b.benfit_title}</h6>
                          <p className="benefit-text mb-0">{b.benfit_description}</p>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </Card>

            <Card className="insurance-detail-premium">
              <div className="p-2 p-md-3 border-bottom">
                <h5 className="section-title mb-0">Premium Table</h5>
              </div>
              <div className="p-2 p-md-3">
                <div className="premium-table-wrapper">
                  <Table responsive className="premium-table mb-0">
                    <thead>
                      <tr>
                        <th>Age Band</th>
                        <th>Monthly (BD)</th>
                        <th>Quarterly (BD)</th>
                        <th>Half Yearly (BD)</th>
                        <th>Yearly (BD)</th>
                        <th>Sum Assured (BD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(product.product_premium) ? product.product_premium : []).map((p) => (
                        <tr key={p.id}>
                          <td>{p.age}</td>
                          <td>{p.monthly_bd}</td>
                          <td>{p.quarterly_bd}</td>
                          <td>{p.half_yearly_bd}</td>
                          <td>{p.yearly_bd}</td>
                          <td>{p.sum_assured}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Card>
          </Col>

          <Col lg="4">
            <Card className="insurance-detail-eligibility mb-2">
              <div className="p-2 p-md-3 border-bottom">
                <h5 className="section-title mb-0">Eligibility Criteria</h5>
              </div>
              <div className="p-2 p-md-3">
                <Table borderless responsive className="eligibility-table mb-0">
                  <thead>
                    <tr>
                      <th className="label">Particular</th>
                      <th className="value">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="label">Min Entry Age</td>
                      <td className="value">{eligibility?.min_age ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="label">Max Entry Age</td>
                      <td className="value">{eligibility?.max_age ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="label">Max Maturity Age</td>
                      <td className="value">{eligibility?.max_maturity_age ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="label">Eligible Customers</td>
                      <td className="value">{eligibility?.customer_eligibility || "-"}</td>
                    </tr>
                    <tr>
                      <td className="label">Medical Requirements</td>
                      <td className="value">{eligibility?.medical_requirement || "-"}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Card>
            <Card className="insurance-detail-interested">
              <div className="p-2 p-md-3">
                <Button 
                  className="interested-btn w-100" 
                  block
                  onClick={() => setIsLeadModalOpen(true)}
                >
                  I&apos;m interested
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
      <LeadModal
        isOpen={isLeadModalOpen}
        toggle={handleLeadModalClose}
        serviceName="Insurance"
        productName={product.product_name}
        onSubmit={handleLeadSubmit}
      />
    </div>
  );
};

export default InsuranceDetail;
